/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonProps } from "../Button";
import Draw from "ol/interaction/Draw";
import Snap from "ol/interaction/Snap";
import Style from "ol/style/Style";
import { DrawEvent } from "ol/interaction/Draw";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import DoubleClickZoom from "ol/interaction/DoubleClickZoom";
import { Coordinate } from "ol/coordinate";

import { PolygonIcon } from "../../../constants/icons/PolygonIcon";
import { useMap } from "../../../hooks";
import { useControlSection } from "../../layout";
import { InnerButton } from "../InnerButton";
import { ANNOTATION_COLOR } from "../../../constants";
import { FeatureFromGeojson, useFeaturesStore } from "../../../FeaturesStore";
import { positionsFromFeature } from "../../../../utils";
import { makeGeojsonShape } from "../../../../utils/makeGeojsonShape";
import { useVectorSourceContext } from "../../../hooks/incontext/useVectorSourceContext";
import { useDrawSketchGestures } from "src/domains/shared/react-openlayers/Map/hooks/useDrawSketchGestures";
import { useToolGuide } from "../../useToolGuide";
import {
  DRAWING_TOOL_CURSOR,
  DRAW_TOOL_EXIT_CURSOR,
  MAP_TOOL_BUTTON,
  MAP_TOOL_LABEL_KEYS,
} from "src/domains/shared/constants/mapToolButton";

export interface PolygonDrawButtonProps extends ButtonProps {
  /**
   * @description You can get Multipoint feature what was made by callback function.
   */
  onEnd?: (features: FeatureFromGeojson) => void;

  /**
   * @description You can set callback Fn on 'start' event.
   */
  onStart?: () => void;

  /**
   * @default false
   * @description Well... Sometimes you need this drawing tool with using server waht containes DB. if 'onCanvas' set false, react-openlayer will not draw feature on canvas.
   */
  onCanvas?: boolean;
  color?: keyof typeof ANNOTATION_COLOR;
}

export function PolygonDrawButton({
  onEnd,
  onStart,
  onClick,
  onCanvas = false,
  color = "BLUE",
  disabled,
  ...props
}: PolygonDrawButtonProps) {
  const { t } = useTranslation("timeSeries");
  const map = useMap();
  const id = useId();
  const buttonId = `controlbutton-${id}`;
  const { selectButton, selectedButtonId } = useControlSection();
  const { getVectorSources, vectorSourcesCount } = useVectorSourceContext();

  const isActive = buttonId === selectedButtonId;

  const { addGeoJson } = useFeaturesStore();
  const drawRef = useRef<Draw | null>(null);
  const snapRef = useRef<Snap | null>(null);
  const sketchSourceRef = useRef<VectorSource | null>(null);
  const sketchLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

  const exitDrawing = useCallback(() => {
    if (drawRef.current) {
      map.removeInteraction(drawRef.current);
    }
    if (snapRef.current) {
      map.removeInteraction(snapRef.current);
    }
    if (sketchLayerRef.current) {
      map.removeLayer(sketchLayerRef.current);
      sketchLayerRef.current = null;
    }
    if (sketchSourceRef.current) {
      sketchSourceRef.current.clear();
      sketchSourceRef.current = null;
    }
    map.getInteractions().forEach((i) => {
      if (i instanceof DoubleClickZoom) i.setActive(true);
    });
    selectButton("");
    map.setProperties({ isDrawing: false });
    map.getViewport().style.cursor = DRAW_TOOL_EXIT_CURSOR;
  }, [map, selectButton]);

  const { drawOptions, pointCount } = useDrawSketchGestures({
    map,
    isActive,
    kind: "polygon",
    getDraw: () => drawRef.current,
    onExit: exitDrawing,
  });
  useToolGuide({ isActive, kind: "polygon", pointCount });

  const startDrawing = () => {
    if (onClick) {
      onClick();
    }
    if (onStart) {
      onStart();
    }
    map.setProperties({ isDrawing: true });
    map.getViewport().style.cursor = DRAWING_TOOL_CURSOR;
    map.getInteractions().forEach((i) => {
      if (i instanceof DoubleClickZoom) i.setActive(false);
    });

    if (!sketchSourceRef.current) {
      sketchSourceRef.current = new VectorSource();
    }
    if (!sketchLayerRef.current) {
      sketchLayerRef.current = new VectorLayer({
        source: sketchSourceRef.current,
      });
      map.addLayer(sketchLayerRef.current);
    }
  };

  const drawing = useCallback(
    (event: DrawEvent) => {
      const feature = event.feature;

      const newPosition = positionsFromFeature(feature, true) as Coordinate[][];

      const newGeoJson = makeGeojsonShape(
        {
          type: "Polygon",
          coordinates: newPosition,
        },
        {
          type: "polygon",
          original_type: "polygon",
          isSelected: true,
        }
      );

      if (onEnd) {
        onEnd(newGeoJson);
      }
      if (onCanvas) {
        addGeoJson(newGeoJson);
      }

      exitDrawing();
    },
    [onEnd, onCanvas, addGeoJson, exitDrawing]
  );

  // onEnd·addGeoJson 정체성이 바뀌어도 활성 Draw 를 새로 만들지 않는다 — 그리는 중인 스케치가 사라지지 않도록 최신 콜백은 ref 로 읽는다
  const latestDrawing = useRef(drawing);
  useLayoutEffect(() => {
    latestDrawing.current = drawing;
  });
  const [handleDrawEnd] = useState(
    () => (event: DrawEvent) => latestDrawing.current(event)
  );

  useEffect(() => {
    if (isActive) {
      const vectorSources = getVectorSources();

      map.getInteractions().forEach((i) => {
        if (i instanceof DoubleClickZoom) {
          i.setActive(false);
        }
      });

      if (drawRef.current) {
        drawRef.current.un("drawend", handleDrawEnd);
        map.removeInteraction(drawRef.current);
      }
      if (snapRef.current) {
        map.removeInteraction(snapRef.current);
        snapRef.current = null;
      }

      if (!sketchSourceRef.current) {
        sketchSourceRef.current = new VectorSource();
      }

      const sketchStyle = new Style({
        zIndex: 1000,
        stroke: new Stroke({
          color: ANNOTATION_COLOR[color].stroke(1),
          width: 2,
        }),
        fill: new Fill({
          color: ANNOTATION_COLOR[color].fill(0.2),
        }),
      });

      if (vectorSources.length > 0) {
        const traceSource = vectorSources[0];

        drawRef.current = new Draw({
          type: "Polygon",
          source: sketchSourceRef.current!,
          trace: true as any,
          traceSource: traceSource as any,
          style: sketchStyle,
          ...drawOptions,
        });
        drawRef.current.on("drawend", handleDrawEnd);

        snapRef.current = new Snap({
          source: traceSource,
        });

        map.addInteraction(drawRef.current);
        map.addInteraction(snapRef.current);
      } else {
        drawRef.current = new Draw({
          type: "Polygon",
          source: sketchSourceRef.current!,
          style: sketchStyle,
          ...drawOptions,
        });
        drawRef.current.on("drawend", handleDrawEnd);

        map.addInteraction(drawRef.current);
      }
    } else {
      if (drawRef.current) {
        drawRef.current.un("drawend", handleDrawEnd);
        map.removeInteraction(drawRef.current);
        drawRef.current = null;
      }
      if (snapRef.current) {
        map.removeInteraction(snapRef.current);
        snapRef.current = null;
      }
      if (sketchLayerRef.current) {
        map.removeLayer(sketchLayerRef.current);
        sketchLayerRef.current = null;
      }
      if (sketchSourceRef.current) {
        sketchSourceRef.current.clear();
        sketchSourceRef.current = null;
      }
      map.getInteractions().forEach((i) => {
        if (i instanceof DoubleClickZoom) i.setActive(true);
      });
      map.getViewport().style.cursor = DRAW_TOOL_EXIT_CURSOR;
    }
  }, [
    isActive,
    vectorSourcesCount,
    map,
    color,
    getVectorSources,
    handleDrawEnd,
    drawOptions,
  ]);

  return (
    <Button
      id={buttonId}
      isDisabled={disabled}
      disabled={disabled}
      hasPopup
      popupText={t(MAP_TOOL_LABEL_KEYS.polygon)}
      onClick={() => {
        if (isActive) {
          exitDrawing();
        } else {
          selectButton(buttonId);
          startDrawing();
        }
      }}
      isActive={isActive}
      aria-pressed={isActive}
      {...props}
    >
      <InnerButton isActive={isActive}>
        <PolygonIcon
          size={MAP_TOOL_BUTTON.ICON_PX}
          color={isActive ? MAP_TOOL_BUTTON.activeIcon : MAP_TOOL_BUTTON.idleIcon}
        />
      </InnerButton>
    </Button>
  );
}
